import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  FileIcon,
  ImageIcon,
  PaperclipIcon,
  PaperPlaneTiltIcon,
  XIcon,
} from "@phosphor-icons/react";
import { Controller, useForm } from "react-hook-form";

import { useValidationError } from "@/contexts/ValidationErrorContext";
import useConversation from "@/hooks/useConversation";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { validateMessage } from "@/utils/messageValidation";
import { reportViolation } from "@/utils/violationReporting";

const MAX_FILE_SIZE = 10 * 1024 * 1024;

interface MessageInputProps {
  disabled?: boolean;
}

function MessageInput({ disabled = false }: MessageInputProps = {}) {
  const { handleSendMessage, handleSendMediaMessage, conversation } =
    useConversation();
  const { authenticatedAxios, user } = useFirebaseContext();
  const { setValidationError, validationError } = useValidationError();
  const [file, setFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [fileSizeError, setFileSizeError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      message: "",
    },
  });

  const message = watch("message");

  const imagePreviewUrl = useMemo(() => {
    if (file && file.type.startsWith("image/")) {
      return URL.createObjectURL(file);
    }
    return null;
  }, [file]);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const validateFileSize = useCallback((selected: File): boolean => {
    if (selected.size > MAX_FILE_SIZE) {
      setFileSizeError(
        `File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB.`
      );
      return false;
    }
    setFileSizeError("");
    return true;
  }, []);

  const performSend = async (messageText: string, attachedFile: File | null) => {
    if (!conversation) return;

    setIsSending(true);
    try {
      if (attachedFile) {
        handleSendMediaMessage(attachedFile);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        if (imageInputRef.current) {
          imageInputRef.current.value = "";
        }
        setFile(null);
      }

      if (messageText.trim() === "") {
        reset({ message: "" });
        setValidationError(null);
        return;
      }

      const validation = validateMessage(messageText);

      if (!validation.isValid) {
        let recentMessages: Array<{
          author: string;
          body: string;
          dateCreated?: string;
          sid: string;
        }> = [];
        try {
          const messageHistory = await conversation.getMessages(5);
          if (messageHistory?.items) {
            recentMessages = messageHistory.items.map((msg) => ({
              author: msg.author || "unknown",
              body: msg.body || "",
              dateCreated: msg.dateCreated?.toISOString(),
              sid: msg.sid,
            }));
          }
        } catch (error) {
          console.error(
            "Failed to fetch recent messages for violation report:",
            error
          );
        }

        const violationDetails = {
          attemptedMessage: messageText,
          violations: validation.violations,
          conversationSid: conversation.sid,
          recentMessages,
        };

        await reportViolation(
          authenticatedAxios,
          "System",
          user?.uid as string,
          "message_validation",
          JSON.stringify(violationDetails, null, 2)
        );

        setValidationError(
          "Please keep all communication within the app. Messages cannot contain email addresses or phone numbers. Please remove this information and try again."
        );

        return;
      }

      setValidationError(null);
      handleSendMessage(messageText);
      reset({ message: "" });
    } finally {
      setIsSending(false);
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 0);
    }
  };

  const handleSend = (data: { message: string }) => {
    if (
      (data.message.trim().length > 0 || file) &&
      !isSending &&
      conversation
    ) {
      void performSend(data.message, file);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) {
      const selectedFile = event.target.files[0];
      if (!validateFileSize(selectedFile)) {
        if (imageInputRef.current) {
          imageInputRef.current.value = "";
        }
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.[0]) {
      const selectedFile = event.target.files[0];
      if (!validateFileSize(selectedFile)) {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);

      if (e.dataTransfer.files?.length) {
        const droppedFile = e.dataTransfer.files[0];
        if (
          droppedFile.type.startsWith("image/") ||
          droppedFile.type === "application/pdf"
        ) {
          if (!validateFileSize(droppedFile)) {
            return;
          }
          setFile(droppedFile);
        }
      }
    },
    [validateFileSize]
  );

  return (
    <Stack
      direction="column"
      gap={0.5}
      alignItems="flex-start"
      px={0}
      py={{
        xs: 1.5,
        md: 0,
      }}
    >
      {fileSizeError ? (
        <Typography
          variant="text11L"
          color="error.main"
          sx={{ textAlign: "center" }}
        >
          {fileSizeError}
        </Typography>
      ) : null}
      <Card
        variant="outlined"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          borderRadius: 2,
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          maxHeight: 400,
          width: "100%",
          borderColor: validationError ? "error.main" : undefined,
          position: "relative",
        }}
      >
        {isDragActive ? (
          <Box
            sx={{
              position: "absolute",
              zIndex: 10,
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(25, 118, 210, 0.08)",
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              fontWeight: 600,
              fontSize: 20,
              color: "#1976d2",
            }}
          >
            Drop image or file here
          </Box>
        ) : null}
        {file ? (
          <Box mt={1} p={1} display="flex" alignItems="center" width="100%">
            {file.type.startsWith("image/") && imagePreviewUrl ? (
              <Box
                component="img"
                src={imagePreviewUrl}
                alt="preview"
                sx={{
                  marginRight: 1,
                  borderRadius: 0.5,
                  width: 100,
                  height: 100,
                  objectFit: "contain",
                }}
              />
            ) : (
              <>
                <FileIcon
                  size={16}
                  weight="bold"
                  style={{
                    marginRight: 8,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    ml: 1,
                  }}
                >
                  {file.name}
                </Typography>
              </>
            )}
            <IconButton
              aria-label="remove file"
              onClick={() => {
                setFile(null);
                setFileSizeError("");
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
                if (imageInputRef.current) {
                  imageInputRef.current.value = "";
                }
              }}
              disabled={disabled || isSending}
              size="small"
              sx={{
                ml: 1,
                width: 24,
                height: 24,
              }}
            >
              <XIcon size={16} weight="bold" />
            </IconButton>
          </Box>
        ) : null}
        <Controller
          name="message"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              inputRef={(e) => {
                field.ref(e);
                messageInputRef.current = e;
              }}
              placeholder="Send a message..."
              fullWidth
              multiline
              minRows={1}
              maxRows={5}
              inputProps={{
                maxLength: 1000,
              }}
              variant="standard"
              disabled={disabled || isSending}
              sx={{
                "& .MuiInputBase-input": {
                  maxHeight: "unset !important",
                },
                "& .MuiInputBase-root": {
                  boxShadow: "none",
                  background: "none",
                },
                "& .MuiInput-underline:before, & .MuiInput-underline:after": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline.Mui-focused:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline.Mui-error:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-root.Mui-disabled": {
                  borderBottom: "none",
                  backgroundColor: "transparent",
                },
                "& .MuiInputBase-root.MuiInput-root.Mui-disabled:before": {
                  borderBottom: "none !important",
                },
              }}
              onChange={(e) => {
                field.onChange(e);
                setValidationError(null);
                setFileSizeError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSubmit(handleSend)();
                }
              }}
            />
          )}
        />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Stack direction="row" spacing={0.5}>
            <IconButton
              aria-label="attach file"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isSending}
              sx={{ m: -1 }}
            >
              <PaperclipIcon size={16} weight="bold" />
            </IconButton>
            <IconButton
              aria-label="attach image"
              onClick={() => imageInputRef.current?.click()}
              disabled={disabled || isSending}
              sx={{ m: -1 }}
            >
              <ImageIcon size={16} weight="bold" />
            </IconButton>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.txt,video/*,audio/*,application/zip,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageChange}
            />
          </Stack>
          <IconButton
            aria-label="send message"
            sx={{
              m: -1,
              color:
                isSending || message?.length > 0 || file
                  ? "secondary.main"
                  : "secondary.light",
            }}
            disabled={disabled || isSending || (message?.length === 0 && !file)}
            onClick={() => void handleSubmit(handleSend)()}
          >
            {isSending ? (
              <CircularProgress size={16} sx={{ color: "secondary.main" }} />
            ) : (
              <PaperPlaneTiltIcon size={16} weight="bold" />
            )}
          </IconButton>
        </Stack>
      </Card>
    </Stack>
  );
}

export default MessageInput;
